<template>
  <div class="mx-auto max-w-screen-xl">
    <div v-if="error">{{ error.message }}</div>
    <div v-else>
      <p v-if="!data">Loading...</p>
      <div v-else>
        <div v-if="data.display_landing_page">
          <section class="bg-white dark:bg-transparent mb-16 md:mb-0">
            <div
              class="gap-8 items-center py-0 md:py-8 mx-auto max-w-screen-xl xl:gap-16 md:grid md:grid-cols-2 sm:py-16 lg:px-0 min-h-96"
            >
              <div>
                <BreadCrumbIndicator></BreadCrumbIndicator>
                <h1 class="mb-4 md:mt-2 tracking-tight text-gray-900 dark:text-white leading-10 text-6xl">
                  {{ data.title }}
                </h1>
                <p class="mb-6 text-gray-500 dark:text-gray-300 text-lg md:text-2xl">
                  {{ data.description }}
                </p>
                <!-- <a href="#" class="text-gray-900 dark:text-white underline font-medium text-sm">
                {{ $t('Read our guide') }}
              </a> -->
              </div>
              <SmartImg
                class="w-full rounded-lg bg-gray-300 dark:bg-gray-600 h-full"
                :src="data.featured_image"
                :alt="`${data.name} image`"
              ></SmartImg>
            </div>
          </section>
          <Carousel
            v-if="attractionsCarousel?.identities.values.length > 3"
            :title="attractionsCarousel.name_alias"
            :itemType="'identity-B'"
            :items="attractionsCarousel.identities.values"
            :URL="attractionsCarousel.URL"
            :buttonLabel="attractionsCarousel.buttonLabel"
            class="my-16 md:my-5 md:py-8"
          />
          <Carousel
            v-if="sightseeingCarousel"
            :title="sightseeingCarousel.label"
            :itemType="'activity'"
            :items="[]"
            :searchParams="sightseeingCarousel.searchParams"
            :URL="sightseeingCarousel.URL"
            :buttonLabel="sightseeingCarousel.buttonLabel"
            class="my-16 md:my-5 md:py-8"
          />
          <Carousel
            v-if="dayTripsCarousel?.identities.values.length > 3"
            :title="dayTripsCarousel.name_alias"
            :itemType="'identity-B'"
            :items="dayTripsCarousel.identities.values"
            :URL="dayTripsCarousel.URL"
            :buttonLabel="dayTripsCarousel.buttonLabel"
            class="my-16 md:my-5 md:py-8"
          />
          <Carousel
            v-if="privateToursActivitiesCarousel"
            :title="privateToursActivitiesCarousel?.label"
            :itemType="'activity'"
            :items="[]"
            :searchParams="privateToursActivitiesCarousel?.searchParams"
            :URL="privateToursActivitiesCarousel?.URL"
            :buttonLabel="privateToursActivitiesCarousel.buttonLabel"
            class="my-16 md:my-5 md:py-8"
          />
          <Carousel
            v-if="outdoorActivitiesCarousel?.identities.values.length > 3"
            :title="outdoorActivitiesCarousel.name_alias"
            :itemType="'identity-B'"
            :items="outdoorActivitiesCarousel.identities.values"
            :URL="outdoorActivitiesCarousel.URL"
            :buttonLabel="outdoorActivitiesCarousel.buttonLabel"
            class="my-16 md:my-5 md:py-8"
          />
          <Carousel
            v-if="foodAndDrinkCarousel"
            :title="foodAndDrinkCarousel?.label"
            :itemType="'activity'"
            :items="[]"
            :searchParams="foodAndDrinkCarousel.searchParams"
            :URL="foodAndDrinkCarousel.URL"
            :buttonLabel="foodAndDrinkCarousel.buttonLabel"
            class="my-16 md:my-5 md:py-8"
          />
          <Carousel
            v-if="museumsCarousel?.identities?.values?.length > 3"
            :title="museumsCarousel.name_alias"
            :itemType="'identity-B'"
            :items="museumsCarousel.identities.values"
            :URL="museumsCarousel.URL"
            :buttonLabel="museumsCarousel.buttonLabel"
            class="my-16 md:my-5 md:py-8"
          />
          <div v-if="topBlogPost" class="pt-10">
            <BlogBigCard :post="topBlogPost" :cityName="data.name" />
          </div>
          <section v-if="data.blog_posts.length > 0" class="pt-10">
            <ArticlesSection :cardVersion="1" :array="data.blog_posts" />
          </section>
          <Carousel
            :title="$t('Categories')"
            itemType="category"
            :items="data.top_categories"
            class="my-16 md:my-5 md:py-8"
          />
          <MapComponent
            v-if="data.showMap"
            :cityName="route.params.city_name"
            heightClass="h-[70vh] mt-10"
          ></MapComponent>
          <NuxtLinkLocale
            :to="`/s/${route.params.city_name}/map`"
            class="justify-end mt-2 mb-5 flex flex-row items-center ts-link"
          >
            {{ $t('{cityName} Attractions Map', { cityName: data.tname }) }}
            <img class="icon" src="/img/icons/chevron_right.svg" />
          </NuxtLinkLocale>
          <div class="py-5">
            <CityListPost
              v-if="data.headless_content_city_list"
              :content="data.headless_content_city_list"
              :searchPageUrl="viewAllActivitiesURL"
              :cityDisplayName="data.tname"
            ></CityListPost>
          </div>
          <div class="md:px-0">
            <MoreActivities :cityName="data.name" :URL="viewAllActivitiesURL" />
          </div>
          <div class="py-10 mt-16">
            <CityGuide
              v-if="data.headless_content_city_guide"
              :content="data.headless_content_city_guide"
              :cityDisplayName="data.tname"
            ></CityGuide>
          </div>
          <div class="py-9">
            <TopLinks :sections="data.top_links"></TopLinks>
          </div>
        </div>
        <div v-else>
          <IdentityTop :data="data"></IdentityTop>
          <ActivitySearchAndFilter
            :initial-params="searchParams"
            @update:resultsCount="updateResultsCount"
            @error="handleError"
          />
          <div class="py-9">
            <TopLinks :sections="data.top_links"></TopLinks>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import SmartImg from '~/components/SmartImg.vue';
import { saveItemInCookie } from '~/components/utils';
import { usePopularSearches } from '~/composables/usePopularSearches';
import { handleApiResponse } from '~/server/utils/utils';
import ActivitySearchAndFilter from '../components/ActivitiesSearchAndFilters.vue';

const route = useRoute();
const { locale } = useI18n();
const { setPopularSearches } = usePopularSearches();
const currentLang = locale.value.split('-')[0];
const topBlogPost = ref(null);
const viewAllActivitiesURL = `/search?city_n=${encodeURIComponent(route.params.city_name.toString())}`;
const emit = defineEmits(['passDataToHeader']);

const { data, error } = await handleApiResponse(`/api/explore`, {
  params: {
    type: 'city',
    city_name: route.params.city_name,
    lang: currentLang,
  },
});

let searchParams = ref({});
if (data.value?.search_params) {
  searchParams = ref({
    city_name: data.value?.search_params.cityName,
    filter_ID: data.value?.search_params.filterID,
    lang: data.value?.search_params.lang,
    role: data.value?.search_params.mun,
  });
} else {
  // console.error('Search params not found');
}

//Pass data to header component
if (data.value) {
  emit('passDataToHeader', { popularSearches: data.value.top_links, cityID: data.value.id, role: data.value.role });
  // Save destination key in cookie to show the correct destination in the search bar and homepage
  saveItemInCookie('RVDestinations', data?.value?.destination_key);
}

useHead({
  title: data.value?.title,
  meta: [
    { name: 'title', content: data?.value?.meta_title },
    { name: 'description', content: data?.value?.meta_description },
    { name: 'keywords', content: data?.value?.meta_keywords },
    {
      name: 'robots',
      content: data?.value?.isIndexable
        ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
        : 'noindex, follow',
    },
  ],
});

const rule = useRobotsRule();
rule.value = data?.value?.isIndexable
  ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
  : 'noindex, follow';

useSeoMeta({
  ogType: 'website',
  title: data?.value?.meta_title,
  ogTitle: data?.value?.meta_title,
  description: data?.value?.meta_description,
  ogDescription: data?.value?.meta_description,
  ogImage: data?.value?.featured_image,
  keywords: data?.value?.meta_keywords,
  twitterCard: 'summary_large_image',
});

const attractionsCarousel = data.value?.identities_carousels.values[2];
const dayTripsCarousel = data.value?.identities_carousels.values[7];
const outdoorActivitiesCarousel = data.value?.identities_carousels.values[1];
const museumsCarousel = data.value?.identities_carousels.values[3];
const privateToursActivitiesCarousel = data.value?.private_tours_carousel;
const sightseeingCarousel = data.value?.sightseeing_carousel;
const foodAndDrinkCarousel = data.value?.food_carousel;

const highlightPost = function () {
  if (!data.value.blog_posts) return [];
  let postIndex = data.value.blog_posts.findIndex((blogpost) => blogpost.wp_post_type.includes('things-to-do'));
  if (postIndex === -1) return null;
  let post = data.value.blog_posts.splice(postIndex, 1)[0];
  if (!post) return null;
  post.content = post.excerpt
    .replace(/<h\d[^>]*>[\s\S]*?<\/h\d>/gi, '\n')
    .replace(/<\/?[^>]+(>|$)/g, '')
    .substring(0, 800);
  topBlogPost.value = post;
};

onMounted(() => {
  highlightPost();
});
</script>
